import React, { useState, useEffect } from 'react';
import { CashMarketConfig, CashMarketDirection } from '../services/cashMarketApi';
import { brokerAPI, BrokerStatus, BrokerFeatureToggle } from '../services/brokerApi';

// Create/Edit modal for a single Cash Market config — mirrors SpreadConfigForm.tsx's modal
// pattern now that Cash Market supports multiple named configs per direction instead of one
// fixed row.

interface Props {
  direction: CashMarketDirection;
  existing: CashMarketConfig | null;
  onSubmit: (data: Partial<CashMarketConfig>) => void;
  onCancel: () => void;
}

type FormState = {
  name: string;
  entryX: number | '';
  entryY: number | '';
  slippagePercentage: number | '';
  delaySeconds: number | '';
  investAmountK: number | '';
  noTradeOpenPct: number | '';
  noTradeOlPct: number | '';
  noTradePcPct: number | '';
  noTradeIntPct: number | '';
  noTradeOpenEnabled: boolean;
  noTradeOlEnabled: boolean;
  noTradePcEnabled: boolean;
  noTradeIntEnabled: boolean;
  slPercentage: number | '';
  targetPercentage: number | '';
  tslPercentage: number | '';
  roiExitPercentage: number | '';
  broker: string;
};

const fromConfig = (c: CashMarketConfig | null): FormState => ({
  name: c?.name || '',
  // X/Y are whole numbers only — the DB column is DECIMAL, which can come back as "9.00" and
  // display with trailing zeros unless rounded here.
  entryX: c?.entry_x !== undefined && c?.entry_x !== null ? Math.round(Number(c.entry_x)) : 0,
  entryY: c?.entry_y !== undefined && c?.entry_y !== null ? Math.round(Number(c.entry_y)) : 0,
  slippagePercentage: c?.slippage_percentage ?? 0.1, delaySeconds: c?.delay_seconds ?? 3,
  investAmountK: c?.invest_amount_k ?? 0,
  noTradeOpenPct: c?.no_trade_open_pct ?? 0, noTradeOlPct: c?.no_trade_ol_pct ?? 0,
  noTradePcPct: c?.no_trade_pc_pct ?? 0, noTradeIntPct: c?.no_trade_int_pct ?? 0,
  noTradeOpenEnabled: !!c?.no_trade_open_enabled, noTradeOlEnabled: !!c?.no_trade_ol_enabled,
  noTradePcEnabled: !!c?.no_trade_pc_enabled, noTradeIntEnabled: !!c?.no_trade_int_enabled,
  slPercentage: c?.sl_percentage ?? 0, targetPercentage: c?.target_percentage ?? 0,
  tslPercentage: c?.tsl_percentage ?? 0, roiExitPercentage: c?.roi_exit_percentage ?? 0,
  broker: c?.broker || 'DHAN',
});

const num = (v: number | ''): number => (v === '' ? 0 : v);

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'white', borderRadius: '8px', padding: '20px', width: '520px',
  maxHeight: '90vh', overflowY: 'auto',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '4px' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' };
const sectionStyle: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', marginBottom: '12px' };

const CashMarketConfigForm: React.FC<Props> = ({ direction, existing, onSubmit, onCancel }) => {
  const [form, setForm] = useState<FormState>(fromConfig(existing));
  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  const isBrokerEnabledFor = (broker: string) =>
    featureToggles.find(t => t.broker === broker && t.feature === 'CASH_MARKET')?.is_enabled ?? (broker === 'DHAN');

  useEffect(() => {
    brokerAPI.list().then(res => setBrokers(res.data.filter(b => b.implemented))).catch(() => {});
    brokerAPI.listFeatures().then(res => setFeatureToggles(res.data)).catch(() => {});
  }, []);

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const numField = (label: string, key: keyof FormState, step = '1') => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type="number" step={step} style={inputStyle}
        value={form[key] as number | ''}
        onChange={e => set(key, (e.target.value === '' ? '' : Number(e.target.value)) as any)}
      />
    </div>
  );

  // Whole numbers only (X/Y) — rounds on every keystroke instead of allowing a decimal.
  const intField = (label: string, key: keyof FormState) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type="number" step="1" style={inputStyle}
        value={form[key] as number | ''}
        onChange={e => set(key, (e.target.value === '' ? '' : Math.round(Number(e.target.value))) as any)}
      />
    </div>
  );

  const toggleField = (label: string, pctKey: keyof FormState, enabledKey: keyof FormState) => (
    <div>
      <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
        <input
          type="checkbox" checked={form[enabledKey] as boolean}
          onChange={e => set(enabledKey, e.target.checked as any)}
        />
        {label}
      </label>
      <input
        type="number" step="0.1" style={{ ...inputStyle, opacity: form[enabledKey] ? 1 : 0.5 }}
        value={form[pctKey] as number | ''} disabled={!form[enabledKey]}
        onChange={e => set(pctKey, (e.target.value === '' ? '' : Number(e.target.value)) as any)}
      />
    </div>
  );

  const handleSubmit = () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    onSubmit({
      name: form.name.trim(),
      direction,
      entry_x: num(form.entryX), entry_y: num(form.entryY),
      slippage_percentage: num(form.slippagePercentage), delay_seconds: num(form.delaySeconds),
      invest_amount_k: num(form.investAmountK),
      no_trade_open_pct: num(form.noTradeOpenPct), no_trade_ol_pct: num(form.noTradeOlPct),
      no_trade_pc_pct: num(form.noTradePcPct), no_trade_int_pct: num(form.noTradeIntPct),
      no_trade_open_enabled: form.noTradeOpenEnabled, no_trade_ol_enabled: form.noTradeOlEnabled,
      no_trade_pc_enabled: form.noTradePcEnabled, no_trade_int_enabled: form.noTradeIntEnabled,
      sl_percentage: num(form.slPercentage), target_percentage: num(form.targetPercentage),
      tsl_percentage: num(form.tslPercentage), roi_exit_percentage: num(form.roiExitPercentage),
      broker: form.broker,
    });
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h4 style={{ marginTop: 0, marginBottom: '14px', fontSize: '15px', fontWeight: 700 }}>
          {existing ? 'Edit Config' : 'New Config'} — {direction === 'BUY_SELL' ? 'BUY → SELL' : 'SELL → BUY'}
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '10px', marginBottom: '12px' }}>
          <div>
            <label style={labelStyle}>Name</label>
            <input type="text" style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Morning Momentum" />
          </div>
          <div>
            <label style={labelStyle}>Broker</label>
            <select style={inputStyle} value={form.broker} onChange={e => set('broker', e.target.value)}>
              <option value="DHAN">Dhan</option>
              {brokers.filter(b => b.broker !== 'DHAN').map(b => (
                <option key={b.broker} value={b.broker}>{b.broker.replace('_', ' ')}{!isBrokerEnabledFor(b.broker) ? ' (disabled)' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Entry</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            {intField('X', 'entryX')}
            {intField('Y', 'entryY')}
            {numField('Slippage (%)', 'slippagePercentage', '0.01')}
            {numField('Delay (s)', 'delaySeconds')}
            {numField('Invest (₹, in Lakhs)', 'investAmountK', '0.1')}
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>No Trade Zone</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {toggleField('O (%)', 'noTradeOpenPct', 'noTradeOpenEnabled')}
            {toggleField(direction === 'BUY_SELL' ? 'O-L (%)' : 'O-H (%)', 'noTradeOlPct', 'noTradeOlEnabled')}
            {toggleField('PC (%)', 'noTradePcPct', 'noTradePcEnabled')}
            {toggleField('INT (%)', 'noTradeIntPct', 'noTradeIntEnabled')}
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>SL / Target / TSL</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            {numField('SL (%)', 'slPercentage', '0.1')}
            {numField('TGT (%)', 'targetPercentage', '0.1')}
            {numField('TSL (%)', 'tslPercentage', '0.1')}
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Position Details</div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>
            No of stocks isn't set here — it's however many stocks currently pass the live X%/Y% breadth
            test + No Trade Zone at the moment of entry, shown on the config card after each entry.
            ROI (%) isn't set here either — it's a live readout (total P&L ÷ total invested) shown on
            the config card once positions are open, not a target you type in advance.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', fontSize: '13px', border: '1px solid #9ca3af', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: 'white', border: 'none', borderRadius: '4px', background: '#6f42c1', cursor: 'pointer' }}>
            {existing ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashMarketConfigForm;
